// @flow
import {ActionsObservable, ofType} from 'redux-observable';
import {forkJoin, of} from 'rxjs';
import {debounceTime, map, switchMap} from 'rxjs/operators';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';
import {saveGeneralSettingsApi} from '../configure/general/api';
import {saveAccountApi} from '../profile/api';
import {saveGeneralContact} from '../configure/general/api';
import {
  setInitialConfigApi,
  saveCanalInfApi,
  validateDomainApi,
  getStoreTypesApi,
  saveGoalInformationApi,
  // saveSupportInformationApi,
  getGoalInformationApi,
  getSupportInformationApi,
} from './api';
import {setCurrencyApi} from '../currency/api';
import {saveStoreCountryApi} from '../country/api';
import {LoginSuccess, GetSessionSuccess} from '../auth/duck';

import type {Action} from '../../store/utils';
import type {ActionObservable, StateObservable} from 'redux-observable';
import type {Store, StoreType, MiniStore} from '../../entities';
import type {RootState} from '../index';

type GoalInformation = {
  storeGoal?: string,
  salesFrequency: string,
  storeInterests: string[],
};

export type SetInitialConfigRequest = {
  store?: string,
  storeType?: string,
  firstName?: string,
  lastName?: string,
  email?: string,
  phone?: string,
  whatsappPhone?: string,
  country?: string,
  currency?: string,
  referralChannel?: string,
  subdomainName?: string,
  storeGoal?: string,
  salesFrequency?: string,
  storeInterests?: string[],
  getAssistant?: boolean,
};

type SupportInformation = {
  getAssistance: boolean,
};
export type OnBoardingAction = Action & {
  error?: string,
  domain?: string,
  store?: Store,
  validationResult?: boolean,
  storeTypes?: StoreType[],
  goalInformation?: GoalInformation,
  supportInformation?: SupportInformation,
  initialConfigRequest?: SetInitialConfigRequest,
};

export type OnBoardingState = {
  error?: string,
  isSettingConfig?: boolean,
  storeTypes?: StoreType[],
  isGettingStoreTypes?: boolean,
  validationResult?: boolean,
  isValidating?: boolean,
  isSavingCanalInf?: boolean,
  saveCanalInfSuccess?: boolean,
  isSavingOnboardingInf?: boolean,
  saveOnboardingInfSuccess?: boolean,
  errorOnboarding?: boolean,
  initialConfigSaved?: MiniStore,

  goalInformation?: GoalInformation,

  supportInformation?: SupportInformation,
};

export class ValidateDomain extends ActionCreator<OnBoardingAction> {}
export class ValidateDomainSuccess extends ActionCreator<OnBoardingAction> {}
export class ValidateDomainErrorFn extends ActionCreator<OnBoardingAction> {}

export class SetInitialConfig extends ActionCreator<OnBoardingAction> {}
export class SetInitialConfigSuccess extends ActionCreator<OnBoardingAction> {}
export class SetInitialConfigErrorFn extends ActionCreator<OnBoardingAction> {}

export class SaveCanalInf extends ActionCreator<OnBoardingAction> {}
export class SaveCanalInfSuccess extends ActionCreator<OnBoardingAction> {}
export class SaveCanalInfErrorFn extends ActionCreator<OnBoardingAction> {}

export class GetStoreTypes extends ActionCreator<OnBoardingAction> {}
export class GetStoreTypesSuccess extends ActionCreator<OnBoardingAction> {}
export class GetStoreTypesErrorFn extends ActionCreator<OnBoardingAction> {}

export class SaveOnboardingInf extends ActionCreator<OnBoardingAction> {}
export class SaveOnboardingInfSuccess extends ActionCreator<OnBoardingAction> {}
export class SaveOnboardingInfErrorFn extends ActionCreator<OnBoardingAction> {}

export class GetGoalInformation extends ActionCreator<OnBoardingAction> {}
export class GetGoalInformationSuccess extends ActionCreator<OnBoardingAction> {}
export class GetGoalInformationErrorFn extends ActionCreator<OnBoardingAction> {}

export class GetSupportInformation extends ActionCreator<OnBoardingAction> {}
export class GetSupportInformationSuccess extends ActionCreator<OnBoardingAction> {}
export class GetSupportInformationErrorFn extends ActionCreator<OnBoardingAction> {}

export const actions = {
  setInitialConfig: (payload: OnBoardingAction) =>
    new SetInitialConfig(payload),
  saveCanalInf: (payload: OnBoardingAction) => new SaveCanalInf(payload),
  validateDomain: (payload: OnBoardingAction) => new ValidateDomain(payload),
  getStoreTypes: (payload: OnBoardingAction) => new GetStoreTypes(payload),
  saveOnboardingInf: (payload: OnBoardingAction) =>
    new SaveOnboardingInf(payload),
  getGoalInformation: (payload: OnBoardingAction) =>
    new GetGoalInformation(payload),
  getSupportInformation: (payload: OnBoardingAction) =>
    new GetSupportInformation(payload),
};

export const initialState = {};

export default createReducer<OnBoardingState>(initialState, {
  [ValidateDomain.type]: state => ({
    ...state,
    isValidating: true,
    validationResult: undefined,
  }),
  [ValidateDomainSuccess.type]: (state, action: OnBoardingAction) => ({
    ...state,
    isValidating: false,
    validationResult: action.validationResult,
  }),
  [ValidateDomainErrorFn.type]: state => ({
    ...state,
    isValidating: false,
  }),
  [SetInitialConfig.type]: state => ({
    ...state,
    isSettingConfig: true,
    error: undefined,
  }),
  [SetInitialConfigSuccess.type]: (state, action: OnBoardingAction) => ({
    ...state,
    isSettingConfig: undefined,
    error: undefined,
    isSavingOnboardingInf: false,
    saveOnboardingInfSuccess: true,
    initialConfigSaved: action.store,
  }),
  [SetInitialConfigErrorFn.type]: (state, action: OnBoardingAction) => ({
    ...state,
    error: action.error,
    isSettingConfig: undefined,
    isSavingOnboardingInf: undefined,
    saveOnboardingInfSuccess: undefined,
  }),

  [SaveCanalInf.type]: state => ({
    ...state,
    isSavingCanalInf: true,
    error: undefined,
    saveCanalInfSuccess: undefined,
  }),
  [SaveCanalInfSuccess.type]: state => ({
    ...state,
    isSavingCanalInf: undefined,
    saveCanalInfSuccess: true,
    error: undefined,
  }),
  [SaveCanalInfErrorFn.type]: (state, action: OnBoardingAction) => ({
    ...state,
    error: action.error,
    isSavingCanalInf: undefined,
    saveCanalInfSuccess: false,
  }),

  [GetStoreTypes.type]: state => ({
    ...state,
    isGettingStoreTypes: true,
    error: undefined,
  }),
  [GetStoreTypesSuccess.type]: (state, action: OnBoardingAction) => ({
    ...state,
    isGettingStoreTypes: false,
    storeTypes: action.storeTypes,
  }),
  [GetStoreTypesErrorFn.type]: state => ({
    ...state,
    isGettingStoreTypes: false,
  }),

  [SaveOnboardingInf.type]: (state, action: OnBoardingAction) => ({
    ...state,
    isSavingOnboardingInf: true,
    errorOnboarding: undefined,
    // domain: action.data.domain,
  }),
  [SaveOnboardingInfSuccess.type]: state => ({
    ...state,
    // isSavingOnboardingInf: false,
    // saveOnboardingInfSuccess: true,
    errorOnboarding: undefined,
  }),
  [SaveOnboardingInfErrorFn.type]: (state, action: OnBoardingAction) => ({
    ...state,
    isSavingOnboardingInf: false,
    saveOnboardingInfSuccess: false,
    errorOnboarding: action.error
      ? action.error
      : 'Ocurrio un error inesperado. Por favor, vuelva a completar los campos.',
  }),
  [GetGoalInformation.type]: state => ({
    ...state,
    isGettingGoalInformation: true,
    error: undefined,
  }),
  [GetGoalInformationSuccess.type]: (state, action: OnBoardingAction) => ({
    ...state,
    isGettingGoalInformation: false,
    goalInformation: action.goalInformation,
  }),
  [GetGoalInformationErrorFn.type]: state => ({
    ...state,
    isGettingGoalInformation: false,
  }),
  [GetSupportInformation.type]: state => ({
    ...state,
    isGettingSupportInformation: true,
    error: undefined,
  }),
  [GetSupportInformationSuccess.type]: (state, action: OnBoardingAction) => ({
    ...state,
    isGettingSupportInformation: false,
    supportInformation: action.supportInformation,
  }),
  [GetSupportInformationErrorFn.type]: state => ({
    ...state,
    isGettingSupportInformation: false,
  }),
});

export const getStoreTypesEpic = (action$: ActionObservable) => {
  return action$.pipe(
    ofType(GetStoreTypes.type),
    switchMap(a => {
      return getStoreTypesApi().pipe(
        map((storeTypes: StoreType[]) => {
          return new GetStoreTypesSuccess({storeTypes});
        }),
        buildCatchError(GetStoreTypesErrorFn)
      );
    })
  );
};

export function validateDomainEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(ValidateDomain.type),
    debounceTime(500),
    switchMap(({subdomain}: OnBoardingAction) => {
      // validate subdomain
      if (state$.value?.store?.store?.domains) {
        const firstDomain = state$.value.store.store.domains[0];
        if (firstDomain.domainName) {
          const draftSubdomain = firstDomain.domainName.split('.')[0];
          if (subdomain === draftSubdomain)
            return of(new ValidateDomainSuccess({validationResult: true}));
        }
      }

      return validateDomainApi({subdomainName: subdomain}).pipe(
        map(() => new ValidateDomainSuccess({validationResult: true})),
        buildCatchError(ValidateDomainErrorFn)
      );
    })
  );
}

export function saveCanalInfEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(SaveCanalInf.type),
    switchMap(({channel, isInfluencer, influencer}: OnBoardingAction) =>
      saveCanalInfApi({channel, isInfluencer, influencer}).pipe(
        map(data => new SaveCanalInfSuccess()),
        buildCatchError(SaveCanalInfSuccess, 'Hubo un error inesperado')
      )
    )
  );
}

export function setInitialConfigEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(SetInitialConfig.type, SaveOnboardingInfSuccess.type),

    switchMap(({initialConfigRequest}: OnBoardingAction) => {
      // const domain = state$.value.onBoarding.domain;
      return setInitialConfigApi(initialConfigRequest).pipe(
        map(store => {
          return new SetInitialConfigSuccess({store});
        }),
        buildCatchError(SetInitialConfigErrorFn, 'Hubo un error inesperado')
      );
    })
  );
}

export const saveOnboardingInfEpic = (action$: ActionObservable) => {
  return action$.pipe(
    ofType(SaveOnboardingInf.type),
    switchMap(({data, initialConfigRequest}: OnBoardingAction) => {
      const tasks = [];

      let request = {};
      if (data.generalSettingsRequest.name)
        request = {name: data.generalSettingsRequest.name};
      if (data.generalSettingsRequest.storeTypeId)
        request = {
          ...request,
          storeTypeId: data.generalSettingsRequest.storeTypeId,
        };

      if (data.generalSettingsRequest.storeBusinessModel)
        request = {
          ...request,
          storeBusinessModel: data.generalSettingsRequest.storeBusinessModel,
        };

      console.log({
        saveGeneralSettingsApi: {...request},
      });

      tasks.push(
        saveGeneralSettingsApi({...request}).pipe(
          map(_ => null),
          buildCatchError(
            SaveOnboardingInfErrorFn,
            'Error al guardar información de la tienda'
          )
        )
      );

      request = {};
      if (data.accountSettingsRequest.firstName)
        request = {firstName: data.accountSettingsRequest.firstName};
      if (data.accountSettingsRequest.lastName)
        request = {...request, lastName: data.accountSettingsRequest.lastName};

      console.log({
        saveAccountApi: {...request},
      });
      tasks.push(
        saveAccountApi({...request}).pipe(
          map(_ => null),
          buildCatchError(
            SaveOnboardingInfErrorFn,
            'Error al guardar información del vendedor'
          )
        )
      );

      data.dataContactRequest.forEach(dc => {
        console.log({
          saveGeneralContact: {
            data: dc.data,
            contactTypeId: dc.contactTypeId,
          },
        });
        const obs = saveGeneralContact({
          data: dc.data,
          contactTypeId: dc.contactTypeId,
        }).pipe(
          map(_ => null),
          buildCatchError(
            SaveOnboardingInfErrorFn,
            'Error al guardar información del vendedor'
          )
        );
        tasks.push(obs);
      });

      console.log({
        saveStoreCountryApi: {
          countryId: data.countryRequest.countryId,
        },
      });
      tasks.push(
        saveStoreCountryApi({countryId: data.countryRequest.countryId}).pipe(
          map(_ => null),
          buildCatchError(
            SaveOnboardingInfErrorFn,
            'Error al guardar el pais de la tienda'
          )
        )
      );

      console.log({
        setCurrencyApi: {
          currencyId: data.currencyRequest.currencyId,
        },
      });
      tasks.push(
        setCurrencyApi({currencyId: data.currencyRequest.currencyId}).pipe(
          map(_ => null),
          buildCatchError(
            SaveOnboardingInfErrorFn,
            'Error al guardar la moneda de la tienda'
          )
        )
      );

      console.log({
        saveCanalInfApi: {...data.canalInfRequest},
      });
      tasks.push(
        saveCanalInfApi({...data.canalInfRequest}).pipe(
          map(_ => null),
          buildCatchError(
            SaveOnboardingInfErrorFn,
            'Error al guardar el canal de información'
          )
        )
      );

      console.log({
        saveGoalInformationApi: {...data.goalInfRequest},
      });
      tasks.push(
        saveGoalInformationApi({...data.goalInfRequest}).pipe(
          map(_ => null),
          buildCatchError(
            SaveOnboardingInfErrorFn,
            'Error al guardar la información de la meta'
          )
        )
      );

      /*    tasks.push(
        saveSupportInformationApi({...data.supportInfRequest}).pipe(
          map(_ => null),
          buildCatchError(
            SaveOnboardingInfErrorFn,
            'Error al guardar la información de soporte'
          )
        )
      ); */

      return forkJoin(tasks).pipe(
        map((dataError: any[]) => {
          const e = dataError.find(x => x !== null);
          if (e) return e;
          else return new SaveOnboardingInfSuccess({initialConfigRequest});
        })
      );
    })
  );
};

export function getSupportInformationEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(
      GetSupportInformation.type,
      LoginSuccess.type,
      GetSessionSuccess.type
    ),
    switchMap(() =>
      getSupportInformationApi().pipe(
        map(
          supportInformation =>
            new GetSupportInformationSuccess({
              supportInformation,
            })
        ),
        buildCatchError(GetSupportInformationErrorFn)
      )
    )
  );
}

export function getGoalInformationEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(GetGoalInformation.type, LoginSuccess.type, GetSessionSuccess.type),
    switchMap(() =>
      getGoalInformationApi().pipe(
        map(
          goalInformation =>
            new GetGoalInformationSuccess({
              goalInformation,
            })
        ),
        buildCatchError(GetGoalInformationErrorFn)
      )
    )
  );
}
